import React from 'react';
import { BaseStep, useFormData, BaseStepProps } from '../BaseStep';
import FormSection from '../../../FormSection';
import PillSelector from '../PillSelector';
import { Toggle } from '../Toggle';
import foundersIcon from '../../../../assets/icons/founders.png';
import { useProfile } from '../../../../contexts/ProfileContext';

const founderCountOptions = [
  { value: '1', label: '1' },
  { value: '2+', label: '2+' },
  { value: '3+', label: '3+' },
  { value: 'no-preference', label: 'No preference' },
];

const teamMemberCountOptions = [
  { value: '0', label: '0' },
  { value: '1+', label: '1+' },
  { value: '2+', label: '2+' },
  { value: '3+', label: '3+' },
  { value: 'no-preference', label: 'No preference' },
];

/** Map legacy exact counts to the current “or more” pills. */
const normalizePreferredTeamMembersCount = (raw: string): string => {
  if (raw === '1') return '1+';
  if (raw === '2') return '2+';
  if (raw === '3') return '3+';
  return raw;
};

const founderRepresentationOptions = [
  { key: 'woman', label: 'Woman' },
  { key: 'poc', label: 'POC (person of color)' },
  { key: 'lgbtq', label: 'LGBTQ+' },
  { key: 'immigrant', label: 'Immigrant' },
  { key: 'personWithDisabilities', label: 'Person with disabilities' },
  { key: 'seniorEntrepreneur', label: 'Senior/65+ entrepreneur' },
  { key: 'teenEntrepreneur', label: 'Teen/19- entrepreneur' },
];

const FoundersStep: React.FC<BaseStepProps> = ({
  userType,
  onCreateProfile,
  hasProfile,
  onComplete,
  shouldValidate,
  onValidationHandled,
  onValidationResult,
}) => {
  const { investorProfile } = useProfile();

  // Helper to parse founder representation (handles both JSON string and array formats)
  const parseFounderRepresentation = (
    storedData: string | string[] | undefined
  ): Record<string, boolean> => {
    if (Array.isArray(storedData)) {
      // Convert array to record
      const record: Record<string, boolean> = {};
      founderRepresentationOptions.forEach(opt => {
        record[opt.key] = storedData.includes(opt.key);
      });
      return record;
    }
    if (typeof storedData === 'string' && storedData) {
      try {
        const parsed = JSON.parse(storedData);
        if (typeof parsed === 'object' && parsed !== null) {
          return parsed;
        }
      } catch {
        // If parsing fails, return default
      }
    }
    return {
      woman: false,
      poc: false,
      lgbtq: false,
      immigrant: false,
      personWithDisabilities: false,
      seniorEntrepreneur: false,
      teenEntrepreneur: false,
    };
  };

  const { formData, setFormData } = useFormData({
    preferredFoundersCount: (() => {
      const raw = String(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (investorProfile as any)?.preferredFoundersCount || 'no-preference'
      );
      return raw === '2' ? '2+' : raw;
    })(),
    preferredTeamMembersCount: normalizePreferredTeamMembersCount(
      String(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (investorProfile as any)?.preferredTeamMembersCount || 'no-preference'
      )
    ),
  });

  // State for founder representation toggles
  const [founderRepresentation, setFounderRepresentation] = React.useState<Record<string, boolean>>(
    () => {
      if (investorProfile) {
        return parseFounderRepresentation(
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          (investorProfile as any)?.founderRepresentation
        );
      }
      return {
        woman: false,
        poc: false,
        lgbtq: false,
        immigrant: false,
        personWithDisabilities: false,
        seniorEntrepreneur: false,
        teenEntrepreneur: false,
      };
    }
  );

  // Update formData with founder representation (as array)
  React.useEffect(() => {
    const representationArray = Object.keys(founderRepresentation).filter(
      key => founderRepresentation[key]
    );
    setFormData(prev => ({
      ...prev,
      founderRepresentation: representationArray,
    }));
  }, [founderRepresentation, setFormData]);

  // Track if we need to show validation errors
  const [showErrors, setShowErrors] = React.useState(false);

  // Listen for validation requests from parent
  React.useEffect(() => {
    if (shouldValidate) {
      // Require founder count and team member count selections
      const isValid = !!(
        formData.preferredFoundersCount &&
        formData.preferredFoundersCount !== '' &&
        formData.preferredTeamMembersCount &&
        formData.preferredTeamMembersCount !== ''
      );

      setShowErrors(true);

      // Notify parent about validation result
      if (onValidationResult) {
        onValidationResult(isValid);
      }

      // Notify parent that validation was handled
      if (onValidationHandled) {
        onValidationHandled();
      }
    }
  }, [
    shouldValidate,
    onValidationHandled,
    onValidationResult,
    formData.preferredFoundersCount,
    formData.preferredTeamMembersCount,
  ]);

  // Continuously validate when errors are shown and notify parent when validation passes
  React.useEffect(() => {
    if (showErrors) {
      const isValid = !!(
        formData.preferredFoundersCount &&
        formData.preferredFoundersCount !== '' &&
        formData.preferredTeamMembersCount &&
        formData.preferredTeamMembersCount !== ''
      );
      if (isValid && onValidationResult) {
        onValidationResult(true);
      }
    }
  }, [
    showErrors,
    onValidationResult,
    formData.preferredFoundersCount,
    formData.preferredTeamMembersCount,
  ]);

  // Expose form data to parent component
  React.useEffect(() => {
    if (onCreateProfile) {
      onCreateProfile(formData);
    }
  }, [formData, onCreateProfile]);

  return (
    <BaseStep
      userType={userType}
      onCreateProfile={onCreateProfile}
      hasProfile={hasProfile}
      onComplete={onComplete}
    >
      <FormSection icon={foundersIcon} title="FOUNDERS & TEAM">
        <div>
          <label
            htmlFor="preferredFoundersCount"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Preferred number of founders
          </label>
          <PillSelector
            value={String(formData.preferredFoundersCount || '')}
            onChange={value =>
              setFormData(prev => ({
                ...prev,
                preferredFoundersCount: value,
              }))
            }
            options={founderCountOptions}
          />
          {showErrors &&
            (!formData.preferredFoundersCount || formData.preferredFoundersCount === '') && (
              <p className="text-red-500 text-sm mt-2 text-right">
                Please select preferred number of founders
              </p>
            )}
        </div>

        <div>
          <label
            htmlFor="preferredTeamMembersCount"
            className="block text-sm sm:text-base font-medium text-white mb-2 text-left"
          >
            Preferred number of non-founder team members
          </label>
          <PillSelector
            value={String(formData.preferredTeamMembersCount || '')}
            onChange={value =>
              setFormData(prev => ({
                ...prev,
                preferredTeamMembersCount: value,
              }))
            }
            options={teamMemberCountOptions}
          />
          {showErrors &&
            (!formData.preferredTeamMembersCount || formData.preferredTeamMembersCount === '') && (
              <p className="text-red-500 text-sm mt-2 text-right">
                Please select preferred number of team members
              </p>
            )}
        </div>

        <div>
          <label className="text-sm sm:text-base font-medium text-white mb-4 block text-left">
            Since some investors prefer companies with one or more Founders represented in the
            following, feel free to select any that may apply:
          </label>
          <div className="space-y-3">
            {founderRepresentationOptions.map(option => (
              <Toggle
                key={option.key}
                name={option.key}
                label={option.label}
                value={founderRepresentation[option.key] || false}
                onChange={value =>
                  setFounderRepresentation(prev => ({
                    ...prev,
                    [option.key]: value,
                  }))
                }
              />
            ))}
          </div>
        </div>
      </FormSection>
    </BaseStep>
  );
};

export default FoundersStep;
